import { Fragment, ReactNode, useEffect, useState } from "react";
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { ActionIcon, Box, Button, Card, Checkbox, Grid, Group, Image, Input, Modal, MultiSelect, NavLink, NumberInput, Paper, Select, SimpleGrid, Stack, Switch, Table, TagsInput, Text, TextInput, Textarea, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import "@mantine/dropzone/styles.css";
import { notifications } from "@mantine/notifications";
import { IconBox, IconBuildingBank, IconCoin, IconCreditCard, IconFileText, IconHeadset, IconLayoutGrid, IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand, IconPencil, IconPercentage, IconPhoto, IconPlus, IconSearch, IconTrash, IconUpload, IconUsers } from "@tabler/icons-react";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { useFirestore, useStorage } from "../../helpers/firebaseContext";
import { useLiveCollection } from "./useLive";
import { useCtcCallables, useCtcUser } from "./ctc_helpers";
import CtcDealers from "./ctc_dealers";
import CtcAttachmentTemplates, { useAttachmentTemplates } from "./ctc_attachment_templates";
import { CtcCustomerAttachmentsPanel } from "./ctc_customer_attachments_panel";
import { CtcDynamicFieldsPanel } from "./ctc_dynamic_fields_panel";
import { CTC_ATTACHMENT_NUMBERS } from "../../../../shared/ctcAttachments";
import { isCtcActive } from "../../../../shared/ctcMasterData";
import { CtcPanel, ctcClearable } from "./ctc_panel";
import { CtcRangesEditor, CtcRangesSummary, ctcRangeLines } from "./ctc_ranges_editor";
import { ctcRangesCheck } from "../../../../shared/ctcCommission";
import { ctcCustomerGroupIds } from "../../../../shared/ctcAccess";

/**
 * Back office master data (legacy "Back Office" menu): customers, brokers, commodities,
 * currencies, commission profiles, bank accounts. Generic table + modal form driven by a
 * field spec per collection. Writes go straight to Firestore under the ctc_backoffice rule.
 * Delete is a soft delete (deleted / deletedAt / deletedBy): the record disappears from every list
 * and dropdown, but the document stays so orders already referencing it keep working.
 */

type FieldType = "text" | "number" | "bool" | "select" | "multiselect" | "tags" | "json" | "textarea" | "image" | "ranges";
interface FieldSpec {
    key: string;
    /** for type "image": Storage folder; the file is saved as {folder}/{docId}/{key}.png and the path stored in the field */
    imageFolder?: string;
    label: string;
    type: FieldType;
    required?: boolean;
    /** `inactive` options (retired / deleted) are offered only when already selected, so editing never drops a value. */
    options?: () => { value: string; label: string; inactive?: boolean }[];
    description?: string;
    listHidden?: boolean;
    /** Extra check on save; return an error message to block saving. `row` is the record being edited. */
    validate?: (value: unknown, row: Record<string, unknown>) => string | undefined;
    /** Show (and validate) the field only when this returns true, e.g. a limit amount only while its toggle is on. */
    visibleWhen?: (row: Record<string, unknown>) => boolean;
    /** Half width: consecutive half fields share a row (e.g. English / Arabic name). */
    half?: boolean;
    /** Right-to-left text (Arabic). */
    rtl?: boolean;
    /** Form section title: consecutive fields with the same section share a titled card. */
    section?: string;
}

/** Consecutive fields grouped by section, in form order (no section = plain fields). */
const sectionsOf = (fields: FieldSpec[]) => fields.reduce<{ section?: string; fields: FieldSpec[] }[]>((groups, f) => {
    const last = groups[groups.length - 1];
    if (last && last.section === f.section) last.fields.push(f);
    else groups.push({ section: f.section, fields: [f] });
    return groups;
}, []);
interface CollectionSpec {
    collection: string;
    label: string;
    pluralLabel: string;
    idField?: string; // use this field as the doc id (currencies use the ISO code)
    fields: FieldSpec[];
    /** Checked before a delete; return a message to block it. */
    beforeDelete?: (row: Record<string, unknown>) => Promise<string | undefined> | string | undefined;
    /** Deletes through the server instead of a client write (when the delete must be atomic with a check). */
    serverDelete?: (row: Record<string, unknown>) => Promise<unknown>;
    /** Extra lines for the delete confirmation (e.g. what else the delete changes). */
    deleteNote?: (row: Record<string, unknown>) => string | undefined;
    /** Starting values for a new record (e.g. defaults chosen elsewhere). */
    newRecord?: () => Record<string, unknown>;
    /** Right-hand column of the form, next to the fields; `setValue` edits the record. */
    sidePanel?: (record: Record<string, unknown>, setValue: (key: string, value: unknown) => void, errors: Record<string, string>) => ReactNode;
    /** Edit modal width (default: wide with a side panel, "lg" without). */
    modalSize?: string;
    /** Checks beyond single fields (e.g. the side panel); errors keyed by field path, shown where that path is edited. */
    validateRecord?: (record: Record<string, unknown>) => Record<string, string>;
}

const IMAGE_MAX_BYTES = 2 * 1024 * 1024; // storage.rules: ctc/customers/** images < 2 MB

/**
 * Image field: a box with the current image (or "No logo") and, once a file is picked, the new one.
 * Click or drop to choose; the file is uploaded on Save. Images can be replaced, not removed.
 */
function ImageField({ label, description, error, path, pending, onPick }: {
    label: string; description?: string; error?: string; path?: string; pending?: File; onPick: (file: File) => void;
}) {
    const storage = useStorage();
    const [currentUrl, setCurrentUrl] = useState<string>();
    const [pendingUrl, setPendingUrl] = useState<string>();
    useEffect(() => {
        let cancelled = false;
        setCurrentUrl(undefined);
        if (path) getDownloadURL(storageRef(storage, path)).then((url) => { if (!cancelled) setCurrentUrl(url); }).catch(() => undefined);
        return () => { cancelled = true; };
    }, [path, storage]);
    useEffect(() => {
        if (!pending) return setPendingUrl(undefined);
        const url = URL.createObjectURL(pending);
        setPendingUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [pending]);
    const shown = pendingUrl ?? currentUrl;
    return (
        <Input.Wrapper label={label} description={description} error={error}>
            <Dropzone mt={6} multiple={false} accept={["image/png", "image/jpeg"]} maxSize={IMAGE_MAX_BYTES}
                onDrop={(files) => files[0] && onPick(files[0])}
                onReject={(rejected) => notifications.show({ color: "red", message: rejected[0]?.errors[0]?.code === "file-too-large" ? "The image must be smaller than 2 MB" : "Choose a PNG or JPEG image" })}>
                <Group justify="center" gap="lg" mih={110} wrap="nowrap" style={{ pointerEvents: "none" }}>
                    {shown ? <Image src={shown} alt={label} mah={80} maw={220} fit="contain" /> : <IconPhoto size={36} color="var(--mantine-color-dimmed)" />}
                    <div>
                        <Text size="sm" fw={500}>{pending ? `New: ${pending.name}` : path ? "Current logo" : "No logo yet"}</Text>
                        <Text size="xs" c="dimmed">{pending ? "Saved when you click Save" : <><IconUpload size={12} /> Click or drop a PNG / JPEG (max 2 MB) to {path ? "replace it" : "add one"}</>}</Text>
                    </div>
                </Group>
            </Dropzone>
        </Input.Wrapper>
    );
}

const get = (row: Record<string, unknown>, path: string): unknown => path.split(".").reduce<unknown>((acc, k) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[k] : undefined), row);
const set = (row: Record<string, unknown>, path: string, value: unknown) => {
    const parts = path.split(".");
    let cur = row;
    parts.slice(0, -1).forEach((k) => { cur[k] = (cur[k] as Record<string, unknown>) ?? {}; cur = cur[k] as Record<string, unknown>; });
    cur[parts[parts.length - 1]] = value;
};

const cell = (value: unknown) => {
    if (value === undefined || value === null || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

function MasterDataTable({ spec }: { spec: CollectionSpec }) {
    const db = useFirestore();
    const storage = useStorage();
    const user = useCtcUser();
    const [pendingImages, setPendingImages] = useState<Record<string, File>>({});
    const { data, loading } = useLiveCollection<Record<string, unknown>>(spec.collection);
    const rows = (data ?? []) as Record<string, unknown>[];
    const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
    const [saving, setSaving] = useState(false);
    const [showRetired, setShowRetired] = useState(false);
    const [search, setSearch] = useState("");
    /** Validation message per field key, shown under the field (cleared when the field changes). */
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const visibleFields = spec.fields.filter((f) => !f.listHidden);

    // Save is disabled while an error shows: live checks (e.g. ranges) or errors from the last attempt not fixed yet
    const hasErrors = !!editing && (Object.keys(fieldErrors).length > 0 ||
        spec.fields.some((f) => !(f.visibleWhen && !f.visibleWhen(editing)) && !!f.validate?.(get(editing, f.key), editing)));

    const save = async () => {
        if (!editing) return;
        const isEmpty = (v: unknown) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
        // Collect every problem per field, so each one shows under its field
        const errors: Record<string, string> = {};
        spec.fields.forEach((f) => {
            if (f.visibleWhen && !f.visibleWhen(editing)) return; // hidden fields aren't required or validated
            const value = get(editing, f.key);
            if (f.required && isEmpty(value)) {
                errors[f.key] = `${f.label} is required`;
            } else if (f.type === "json" && typeof value === "string" && value !== "") {
                // JSON fields must hold parsed data, never the raw text left behind by a failed parse.
                errors[f.key] = "Invalid JSON";
            } else {
                const message = f.validate?.(value, editing);
                if (message) errors[f.key] = message;
            }
        });
        Object.assign(errors, spec.validateRecord?.(editing) ?? {});
        setFieldErrors(errors);
        if (Object.keys(errors).length) {
            notifications.show({ color: "red", title: "Please fix the highlighted fields", message: Object.values(errors).join("\n") });
            return;
        }
        setSaving(true);
        try {
            const id = (editing.id as string | undefined) ?? (spec.idField ? String(get(editing, spec.idField)).toUpperCase() : doc(collection(db, spec.collection)).id);
            const { id: _drop, ...payload } = editing;
            for (const [key, file] of Object.entries(pendingImages)) {
                const field = spec.fields.find((f) => f.key === key);
                const path = `${field?.imageFolder ?? "ctc"}/${id}/${key}.png`;
                await uploadBytes(storageRef(storage, path), file, { contentType: file.type || "image/png" });
                set(payload, key, path);
            }
            await setDoc(doc(db, spec.collection, id), {
                ...payload,
                ...(editing.id ? { updated: serverTimestamp(), updatedBy: user.email } : { created: serverTimestamp(), createdBy: user.email, updated: serverTimestamp(), updatedBy: user.email }),
            }, { merge: true });
            notifications.show({ color: "green", message: "Saved" });
            setPendingImages({});
            setEditing(null);
        } catch (e) {
            console.error("master data save failed", e);
            notifications.show({ color: "red", message: (e as Error).message });
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row: Record<string, unknown>) => {
        const name = cell(row.name_en ?? row.id);
        try {
            const blocked = await spec.beforeDelete?.(row);
            if (blocked) {
                notifications.show({ color: "red", title: `Can't delete ${name}`, message: blocked, autoClose: 8000 });
                return;
            }
            const note = spec.deleteNote?.(row);
            if (!window.confirm(`Delete ${name}?\n\nIt will be hidden everywhere and can't be picked again. Orders that already use it keep working. To hide it only for now, mark it as retired instead.${note ? `\n\n${note}` : ""}`)) return;
            if (spec.serverDelete) {
                await spec.serverDelete(row);
            } else {
                // Soft delete: never remove the document (firestore.rules forbids it)
                await setDoc(doc(db, spec.collection, row.id as string), {
                    deleted: true,
                    deletedAt: serverTimestamp(),
                    deletedBy: user.email,
                    updated: serverTimestamp(),
                    updatedBy: user.email,
                }, { merge: true });
            }
            notifications.show({ color: "green", message: `${name} deleted` });
        } catch (e) {
            console.error("master data delete failed", e);
            notifications.show({ color: "red", message: (e as Error).message });
        }
    };

    // Deleted records never show; retired ones only on request; search matches any shown column (as displayed)
    // Same text the table cell shows (option labels, Yes / No, JSON), so the search finds what users see
    const shown = (row: Record<string, unknown>, f: FieldSpec) => f.type === "select" ? (f.options?.().find((o) => o.value === get(row, f.key))?.label ?? cell(get(row, f.key))) :
        f.type === "multiselect" ? ((get(row, f.key) as string[] | undefined) ?? []).map((id) => f.options?.().find((o) => o.value === id)?.label ?? id).join(", ") :
            f.type === "ranges" ? (ctcRangeLines(get(row, f.key), get(row, "currencyId") as string | undefined) ?? ["Invalid ranges"]).join(" ") : cell(get(row, f.key));
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => r.deleted !== true && (showRetired || !r.retired) && (!q || visibleFields.some((f) => shown(r, f).toLowerCase().includes(q))));

    return (
        <>
            <Group justify="space-between" mb="sm" wrap="wrap">
                <TextInput leftSection={<IconSearch size={14} />} placeholder={`Search ${spec.pluralLabel.toLowerCase()}`} value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={260} {...ctcClearable(search, () => setSearch(""))} />
                <Group gap="md">
                    <Checkbox label="Show retired" checked={showRetired} onChange={(e) => setShowRetired(e.currentTarget.checked)} />
                    <Button leftSection={<IconPlus size={16} />} onClick={() => { setFieldErrors({}); setPendingImages({}); setEditing(spec.newRecord?.() ?? {}); }}>Add {spec.label.toLowerCase()}</Button>
                </Group>
            </Group>
            <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                    <Table.Tr>{visibleFields.map((f) => <Table.Th key={f.key}>{f.label}</Table.Th>)}<Table.Th /></Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {filtered.map((row) => (
                        <Table.Tr key={row.id as string} style={row.retired ? { opacity: 0.5 } : undefined}>
                            {visibleFields.map((f) => <Table.Td key={f.key}>{f.type === "ranges" ? <CtcRangesSummary value={get(row, f.key)} currency={get(row, "currencyId") as string | undefined} /> : f.type === "select" ? (f.options?.().find((o) => o.value === get(row, f.key))?.label ?? cell(get(row, f.key))) : f.type === "multiselect" ? (((get(row, f.key) as string[] | undefined) ?? []).map((id) => f.options?.().find((o) => o.value === id)?.label ?? id).join(", ") || "—") : cell(get(row, f.key))}</Table.Td>)}
                            <Table.Td ta="right">
                                <Group gap="xs" justify="flex-end" wrap="nowrap">
                                    <ActionIcon variant="subtle" onClick={() => { setFieldErrors({}); setPendingImages({}); setEditing(structuredClone(row)); }}><IconPencil size={16} /></ActionIcon>
                                    <ActionIcon variant="subtle" color="red" onClick={() => remove(row)}><IconTrash size={16} /></ActionIcon>
                                </Group>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                    {!filtered.length && <Table.Tr><Table.Td colSpan={visibleFields.length + 1}><Text c="dimmed" size="sm">{loading ? "Loading…" : q ? "No match." : `No ${spec.pluralLabel.toLowerCase()} to display.`}</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
            </Table>

            <Modal opened={!!editing} onClose={() => { if (!saving) { setPendingImages({}); setEditing(null); } }} title={editing?.id ? `Edit ${spec.label.toLowerCase()}` : `New ${spec.label.toLowerCase()}`} size={spec.modalSize ?? (spec.sidePanel ? "90rem" : "lg")}>
                {editing && (
                    <Stack>
                        <Grid gap="lg">
                            <Grid.Col span={{ base: 12, md: spec.sidePanel ? 7 : 12 }}>
                                <Stack>
                                    {sectionsOf(spec.fields).map(({ section, fields }, gi) => {
                                        const body = fields.map((f) => {
                                            if (f.visibleWhen && !f.visibleWhen(editing)) return null;
                                            const value = get(editing, f.key);
                                            const update = (v: unknown) => {
                                                setEditing((prev) => { const next = { ...(prev ?? {}) }; set(next, f.key, v); return next; });
                                                setFieldErrors((prev) => { if (!prev[f.key]) return prev; const next = { ...prev }; delete next[f.key]; return next; });
                                            };
                                            const error = fieldErrors[f.key];
                                            switch (f.type) {
                                                case "bool":
                                                    return <Switch key={f.key} label={f.label} description={f.description} error={error} checked={!!value} onChange={(e) => update(e.currentTarget.checked)} />;
                                                case "number":
                                                    return <NumberInput key={f.key} label={f.label} description={f.description} error={error} required={f.required} value={(value as number) ?? ""} onChange={(v) => update(v === "" ? undefined : Number(v))} decimalScale={4} />;
                                                case "select":
                                                    return <Select key={f.key} label={f.label} description={f.description} error={error} required={f.required} data={(f.options?.() ?? []).filter((o) => !o.inactive || o.value === value).map(({ value: v, label }) => ({ value: v, label }))} value={(value as string) ?? null} onChange={(v) => update(v ?? undefined)} searchable clearable={!f.required} />;
                                                case "multiselect": {
                                                    const selected = Array.isArray(value) ? (value as string[]) : [];
                                                    return <MultiSelect key={f.key} label={f.label} description={f.description} error={error} required={f.required} data={(f.options?.() ?? []).filter((o) => !o.inactive || selected.includes(o.value)).map(({ value: v, label }) => ({ value: v, label }))} value={selected} onChange={(v) => update(v)} searchable clearable />;
                                                }
                                                case "tags": {
                                                    // Free-text list: type or paste a value, Enter / comma / space adds it. An older single value shows as one tag.
                                                    const tags = Array.isArray(value) ? (value as string[]) : typeof value === "string" && value.trim() ? [value.trim()] : [];
                                                    return <TagsInput key={f.key} label={f.label} description={f.description} error={error} required={f.required} value={tags} splitChars={[",", " ", "\n"]}
                                                        placeholder={tags.length ? undefined : "Paste an ID and press Enter"} clearable onChange={(v) => update(v.map((t) => t.trim()).filter(Boolean))} />;
                                                }
                                                case "json":
                                                    return <Textarea key={f.key} label={f.label} description={f.description ?? "JSON"} error={error} required={f.required} autosize minRows={3} value={typeof value === "string" ? value : value === undefined ? "" : JSON.stringify(value, null, 2)} onChange={(e) => update(e.currentTarget.value)} onBlur={(e) => { try { update(JSON.parse(e.currentTarget.value || "null") ?? undefined); } catch { notifications.show({ color: "red", message: `${f.label}: invalid JSON` }); } }} />;
                                                case "ranges":
                                                    return <CtcRangesEditor key={f.key} label={f.label} value={value} onChange={update} error={error}
                                                        currency={get(editing, "currencyId") as string | undefined} isOnRange={get(editing, "isOnRange") === true} />;
                                                case "image":
                                                    return <ImageField key={f.key} label={f.label} description={f.description} error={error} path={typeof value === "string" && value ? value : undefined}
                                                        pending={pendingImages[f.key]} onPick={(file) => setPendingImages((prev) => ({ ...prev, [f.key]: file }))} />;
                                                case "textarea":
                                                    return <Textarea key={f.key} label={f.label} description={f.description} error={error} autosize minRows={2} value={(value as string) ?? ""} onChange={(e) => update(e.currentTarget.value)} />;
                                                default:
                                                    return <TextInput key={f.key} label={f.label} description={f.description} error={error} required={f.required} disabled={!!editing.id && f.key === spec.idField} value={(value as string) ?? ""} onChange={(e) => update(e.currentTarget.value)} dir={f.rtl ? "rtl" : undefined} />;
                                            }
                                        });
                                        // Consecutive half-width fields share a row (two columns, one on small screens)
                                        const laidOut: ReactNode[] = [];
                                        for (let k = 0; k < fields.length; k++) {
                                            if (fields[k].half && body[k]) {
                                                const pair = [body[k]];
                                                while (k + 1 < fields.length && fields[k + 1].half && body[k + 1] && pair.length < 2) pair.push(body[++k]);
                                                laidOut.push(<SimpleGrid key={`row-${fields[k].key}`} cols={{ base: 1, sm: 2 }} spacing="md">{pair}</SimpleGrid>);
                                            } else laidOut.push(body[k]);
                                        }
                                        return section ? (
                                            <Card key={section} withBorder radius="md" padding="md">
                                                <Text fw={600} size="sm" mb="sm">{section}</Text>
                                                <Stack gap="md">{laidOut}</Stack>
                                            </Card>
                                        ) : <Fragment key={`fields-${gi}`}>{laidOut}</Fragment>;
                                    })}
                                </Stack>
                            </Grid.Col>
                            {spec.sidePanel ? (
                                <Grid.Col span={{ base: 12, md: 5 }}>
                                    {spec.sidePanel(editing, (key, v) => {
                                        setEditing((prev) => { const next = { ...(prev ?? {}) }; set(next, key, v); return next; });
                                        // clear errors for what the panel just changed (e.g. attachmentTemplateIds.3)
                                        setFieldErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key && !k.startsWith(`${key}.`))));
                                    }, fieldErrors)}
                                </Grid.Col>
                            ) : null}
                        </Grid>
                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => { setPendingImages({}); setEditing(null); }} disabled={saving}>Cancel</Button>
                            <Button onClick={save} loading={saving} disabled={hasErrors}>Save</Button>
                        </Group>
                    </Stack>
                )}
            </Modal>
        </>
    );
}

/** Back office menu: groups, sections (collection id, or attachments / dealers), icon and one line on what it is. */
const BACK_OFFICE_SECTIONS: { group: string; items: { id: string; label: string; icon: ReactNode; description: string }[] }[] = [
    {
        group: "Customers", items: [
            { id: "ctc_customers", label: "Customers", icon: <IconUsers size={18} />, description: "Banks that place orders: limits, approvals, attachments and logo." },
            { id: "ctc_customer_groups", label: "Customer groups", icon: <IconLayoutGrid size={18} />, description: "Groups of customers, used in reports." },
        ]
    },
    {
        group: "Market", items: [
            { id: "ctc_brokers", label: "Brokers", icon: <IconBuildingBank size={18} />, description: "Where commodities are bought, with their locations." },
            { id: "ctc_commodities", label: "Commodities", icon: <IconBox size={18} />, description: "What is traded, with unit and indicative price." },
            { id: "ctc_currencies", label: "Currencies", icon: <IconCoin size={18} />, description: "Currencies orders and commissions can use." },
        ]
    },
    {
        group: "Finance", items: [
            { id: "ctc_commissions", label: "Commission profiles", icon: <IconPercentage size={18} />, description: "Trading types and how the customer and broker commissions are calculated." },
            { id: "ctc_bank_accounts", label: "Bank accounts", icon: <IconCreditCard size={18} />, description: "Customer and settlement accounts." },
        ]
    },
    {
        group: "Documents & people", items: [
            { id: "attachments", label: "Attachment templates", icon: <IconFileText size={18} />, description: "Texts printed on attachments 1–5, with versions." },
            { id: "dealers", label: "Dealers", icon: <IconHeadset size={18} />, description: "Who can be assigned orders (from the administration Microsoft group)." },
        ]
    },
];
/** Section shown when coming back to Back office while the app is open. */
let lastSection = "ctc_customers";

const COLLAPSED_KEY = "ctcBackOffice:menuCollapsed";

const CtcBackOffice = () => {
    const [section, setSection] = useState(lastSection);
    const [collapsed, setCollapsedState] = useState(() => { try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; } });
    const setCollapsed = (v: boolean) => { setCollapsedState(v); try { localStorage.setItem(COLLAPSED_KEY, v ? "1" : "0"); } catch { /* not remembered */ } };
    const db = useFirestore();
    const { deleteCustomer, deleteCommissionProfile } = useCtcCallables();
    const user = useCtcUser();
    const { templates: attachmentTemplates } = useAttachmentTemplates();
    // New customers start with each attachment's default template
    const customerNewRecord = () => ({
        attachmentTemplateIds: Object.fromEntries(CTC_ATTACHMENT_NUMBERS
            .map((n) => [String(n), attachmentTemplates.find((t) => t.attachmentNumber === n && t.isDefault && !t.retired)?.id])
            .filter(([, id]) => !!id)),
    });
    const customerSidePanel = (record: Record<string, unknown>, setValue: (key: string, value: unknown) => void, errors: Record<string, string>) => (
        <Stack gap="md">
            <CtcCustomerAttachmentsPanel
                value={record.attachmentTemplateIds as Record<string, string> | undefined}
                onChange={(next) => setValue("attachmentTemplateIds", next)}
                templates={attachmentTemplates}
                customerName={record.name_en as string | undefined}
                errors={errors}
            />
            <CtcDynamicFieldsPanel customerId={record.id as string | undefined} />
        </Stack>
    );
    // Every customer must have an active template chosen for all 5 attachments
    const customerValidate = (record: Record<string, unknown>) => {
        const chosen = (record.attachmentTemplateIds ?? {}) as Record<string, string>;
        const errors: Record<string, string> = {};
        CTC_ATTACHMENT_NUMBERS.forEach((n) => {
            const t = attachmentTemplates.find((x) => x.id === chosen[String(n)]);
            if (!t) errors[`attachmentTemplateIds.${n}`] = `Attachment ${n} is required`;
            else if (t.retired) errors[`attachmentTemplateIds.${n}`] = `Attachment ${n}: "${t.name}" is retired, choose another`;
        });
        // "Allow orders only in this currency" means nothing without a currency
        if (record.isRestrictedCurrency === true && !record.currencyId) errors.currencyId = "Choose the currency: orders are allowed only in it";
        return errors;
    };
    const currencies = useLiveCollection<{ id: string; shortName: string; name_en: string; retired?: boolean; deleted?: boolean }>("ctc_currencies").data;
    const commissions = useLiveCollection<{ id: string; name_en: string; retired?: boolean; deleted?: boolean }>("ctc_commissions").data;
    const bankAccounts = useLiveCollection<{ id: string; name_en: string; accountNumber: string; retired?: boolean; deleted?: boolean }>("ctc_bank_accounts").data;
    const groups = useLiveCollection<{ id: string; name_en: string; retired?: boolean; deleted?: boolean }>("ctc_customer_groups").data;
    const customers = useLiveCollection<{ id: string; name_en: string; microsoftGroupId?: string | string[]; commissionIds?: string[]; deleted?: boolean }>("ctc_customers").data;
    const brokers = useLiveCollection<{ id: string; name_en: string; commissionId?: string; deleted?: boolean }>("ctc_brokers").data;
    const commodities = useLiveCollection<{ id: string; retired?: boolean; deleted?: boolean }>("ctc_commodities").data; // menu count only

    type Master = { id: string; retired?: boolean; deleted?: boolean };
    /** Dropdown options for master data: inactive ones are labelled and only offered when already selected. */
    const optionsOf = <T extends Master>(list: T[] | undefined, label: (item: T) => string) => (list ?? []).map((item) => ({
        value: item.id,
        label: `${label(item)}${item.deleted ? " (deleted)" : item.retired ? " (retired)" : ""}`,
        inactive: !isCtcActive(item),
    }));
    const currencyOptions = () => optionsOf(currencies, (c) => `${c.shortName} — ${c.name_en}`);
    const commissionOptions = () => optionsOf(commissions, (c) => c.name_en);
    const bankAccountOptions = () => optionsOf(bankAccounts, (b) => `${b.name_en} (${b.accountNumber})`);
    const groupOptions = () => optionsOf(groups, (g) => g.name_en);

    // A customer with open orders can't be deleted: checked and deleted in one server transaction
    const customerServerDelete = (row: Record<string, unknown>) => deleteCustomer({ customerId: row.id as string });
    // A broker's commission is calculated from its profile, so the profile must not disappear under it
    const commissionBeforeDelete = (row: Record<string, unknown>) => {
        const using = (brokers ?? []).filter((b) => !b.deleted && b.commissionId === row.id).map((b) => b.name_en);
        if (using.length) return `Used by broker${using.length === 1 ? "" : "s"} ${using.join(", ")}. Change their commission profile first, or mark this profile as retired instead.`;
        // Commission profiles are required on a customer: never leave one with none
        const onlyProfile = (customers ?? []).filter((c) => !c.deleted && c.commissionIds?.length === 1 && c.commissionIds[0] === row.id).map((c) => c.name_en);
        return onlyProfile.length ? `It is the only commission profile of ${onlyProfile.join(", ")}. Link another profile to ${onlyProfile.length === 1 ? "that customer" : "those customers"} first.` : undefined;
    };
    // Deleting a profile unlinks it from every customer (retiring keeps the links, so it can come back)
    const customersLinkedTo = (commissionId: unknown) => (customers ?? []).filter((c) => !c.deleted && c.commissionIds?.includes(commissionId as string));
    const commissionDeleteNote = (row: Record<string, unknown>) => {
        const linked = customersLinkedTo(row.id);
        return linked.length ? `It will be removed from ${linked.length} customer${linked.length === 1 ? "" : "s"}: ${linked.map((c) => c.name_en).join(", ")}.` : undefined;
    };
    // Check + unlink + soft delete in one server transaction (the checks above are an early warning only)
    const commissionServerDelete = (row: Record<string, unknown>) => deleteCommissionProfile({ commissionId: row.id as string });

    const named: FieldSpec[] = [
        { key: "name_en", label: "English name", type: "text", required: true, half: true },
        { key: "name_ar", label: "Arabic name", type: "text", required: true, half: true, rtl: true },
    ];
    const flags: FieldSpec[] = [
        { key: "retired", label: "Retired", type: "bool", listHidden: true },
    ];

    const specs: CollectionSpec[] = [
        {
            collection: "ctc_customers", label: "Customer", pluralLabel: "Customers", serverDelete: customerServerDelete, newRecord: customerNewRecord, sidePanel: customerSidePanel, validateRecord: customerValidate, fields: [
                ...named,
                { key: "shortName", label: "Short name", type: "text" },
                { key: "refPrefix", label: "Reference prefix", type: "text", required: true, description: "Order references are {prefix}-{YY}-{sequence}" },
                { key: "authDomains", label: "Email domains", type: "json", required: true, description: 'Entra users from these domains act for this customer, e.g. ["kfh.com"]', listHidden: true },
                {
                    key: "microsoftGroupId", label: "Microsoft group IDs", type: "tags",
                    description: "Object IDs of the Entra groups whose members are this customer's makers/checkers, e.g. a makers group and a checkers group. Paste one and press Enter to add another. Members only see this customer; their maker / checker role still comes from their CTC role. Empty = nobody but admins can create or check its orders. Takes effect on the user's next page refresh.",
                    listHidden: true,
                    validate: (value, row) => {
                        const ids = ctcCustomerGroupIds({ microsoftGroupId: value as string | string[] | undefined });
                        const bad = ids.find((id) => !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(id));
                        if (bad) return `"${bad}" isn't a group Object ID (a GUID like 3f2a1b4c-…)`;
                        // One group = one customer, or its members' customer would be ambiguous
                        for (const id of ids) {
                            const other = (customers ?? []).find((c) => c.id !== row.id && !c.deleted && ctcCustomerGroupIds(c).includes(id));
                            if (other) return `Group ${id} is already used by ${other.name_en}`;
                        }
                        return undefined;
                    },
                },
                {
                    key: "commissionIds", label: "Commission profiles", type: "multiselect", options: commissionOptions, required: true,
                    description: "Profiles this customer's orders may use. New purchase order offers only these.", listHidden: true
                },
                { key: "groupId", label: "Group", type: "select", options: groupOptions, listHidden: true },
                { key: "currencyId", section: "Currency and accounts", label: "Currency", type: "select", options: currencyOptions, listHidden: true },
                { key: "isRestrictedCurrency", section: "Currency and accounts", label: "Allow orders only in this currency", type: "bool", listHidden: true, description: "On: new orders must be in the customer's currency above" },
                { key: "bankAccountId", section: "Currency and accounts", label: "Bank account", type: "select", options: bankAccountOptions, listHidden: true },
                { key: "settlementAccountId", section: "Currency and accounts", label: "Settlement account", type: "select", options: bankAccountOptions, listHidden: true },
                { key: "amountLimit.active", section: "Limits", label: "Enforce amount limit", type: "bool", listHidden: true, description: "On: one order may not exceed the amount below" },
                {
                    key: "amountLimit.amount", section: "Limits", label: "Amount limit", type: "number", listHidden: true,
                    description: "Maximum per order, in the customer's currency (other currencies are converted)",
                    visibleWhen: (row) => get(row, "amountLimit.active") === true,
                    validate: (value) => (typeof value === "number" && value > 0 ? undefined : "Enter an amount limit greater than 0"),
                },
                { key: "beneficiaryLimit.active", section: "Limits", label: "Enforce beneficiary limit", type: "bool", listHidden: true, description: "On: a beneficiary's open orders together may not exceed the amount below" },
                {
                    key: "beneficiaryLimit.amount", section: "Limits", label: "Beneficiary limit", type: "number", listHidden: true,
                    description: "Maximum total of all open orders for one beneficiary number, in the customer's currency",
                    visibleWhen: (row) => get(row, "beneficiaryLimit.active") === true,
                    validate: (value) => (typeof value === "number" && value > 0 ? undefined : "Enter a beneficiary limit greater than 0"),
                },
                { key: "needInternalApproval", section: "Approvals", label: "Need PO internal request approval", type: "bool", listHidden: true, description: "A checker approves new orders before CTC" },
                { key: "needInternalTransferApproval", section: "Approvals", label: "Need PO internal transfer approval", type: "bool", listHidden: true, description: "A checker approves the transfer request" },
                { key: "needBeneficiaryTransferApproval", section: "Approvals", label: "Beneficiary approves transfer (WhatsApp and email)", type: "bool", listHidden: true, description: "The beneficiary always gets Attachment 2 on WhatsApp and email and is asked to approve the transfer. On: only the beneficiary's answer (or the dealer recording it) moves the order. Off: the maker or checker may also request the transfer without waiting; the first one counts" },
                { key: "sellOnBehalfOfBeneficiary", section: "Selling", label: "Customer requests sale on behalf of beneficiary", type: "bool", listHidden: true, description: "The beneficiary is always asked on WhatsApp and email to sell or collect. On: the bank's maker or checker may also request the sale in the portal; the first one counts" },
                { key: "ctcSellOnBehalfOfCustomer", section: "Selling", label: "CTC sells on behalf of customer", type: "bool", listHidden: true, description: "The beneficiary is always asked on WhatsApp and email to sell or collect. On: the CTC dealer may also confirm the sale right away; the first one counts" },
                { key: "isTreasury", section: "Other", label: "Treasury (value date)", type: "bool", listHidden: true, description: "New orders get a Value date field" },
                { key: "sendReminderToCustomer", section: "Other", label: "Remind customer", type: "bool", listHidden: true, description: "Saved for reminders; no reminders are sent yet" },
                { key: "sendReminderToBackOffice", section: "Other", label: "Remind back office", type: "bool", listHidden: true, description: "Saved for reminders; no reminders are sent yet" },
                { key: "logoPath", section: "Documents", label: "Logo for documents and beneficiary emails (PNG or JPEG)", type: "image", imageFolder: "ctc/customers", listHidden: true },
                // Beneficiary emails and the email answer page (functions-ctc/ctc-email-decision.md); the logo above is used there too
                { key: "legalName", section: "Beneficiary emails", label: "Legal name", type: "text", listHidden: true, description: "Footer of the emails; empty = the customer name" },
                {
                    key: "supportPhone", section: "Beneficiary emails", label: "Support phone", type: "text", listHidden: true, half: true,
                    description: "Shown in emails, the answer page and WhatsApp texts; empty = 1855855",
                    validate: (value) => !value || /^[+\d][\d\s()-]{2,24}$/.test(`${value}`.trim()) ? undefined : "Digits, spaces and + - ( ) only",
                },
                {
                    key: "brandColor", section: "Beneficiary emails", label: "Brand colour", type: "text", listHidden: true, half: true,
                    description: "Top stripe and main button, e.g. #49ad33; empty = dark grey. Button text turns dark when white would be hard to read",
                    validate: (value) => !value || /^#[0-9a-fA-F]{6}$/.test(`${value}`.trim()) ? undefined : "Use #RRGGBB, e.g. #49ad33",
                },
                ...flags,
            ],
        },
        { collection: "ctc_customer_groups", label: "Customer group", pluralLabel: "Customer groups", fields: [...named, ...flags] },
        {
            collection: "ctc_brokers", label: "Broker", pluralLabel: "Brokers", fields: [
                ...named,
                { key: "email", label: "Email", type: "text" },
                { key: "mobile_code", label: "Dial code", type: "text", listHidden: true },
                { key: "mobile", label: "Mobile", type: "text" },
                { key: "country", label: "Country", type: "text", listHidden: true },
                { key: "currencyId", label: "Currency", type: "select", required: true, options: currencyOptions },
                { key: "commissionId", label: "Commission profile", type: "select", options: commissionOptions },
                { key: "language", label: "Language", type: "select", options: () => [{ value: "en", label: "English" }, { value: "ar", label: "Arabic" }], listHidden: true },
                {
                    key: "locations", label: "Locations", type: "json", required: true, listHidden: true,
                    description: '[{"id":"wh1","name_en":"Warehouse 1","name_ar":"المستودع 1"}]',
                    validate: (value) => {
                        if (!Array.isArray(value)) return "Locations must be a list: [{ \"id\": …, \"name_en\": …, \"name_ar\": … }]";
                        const list = value as { id?: unknown; name_en?: unknown; name_ar?: unknown }[];
                        if (list.some((l) => !l || typeof l.id !== "string" || !l.id.trim())) return "Every location needs an id, e.g. \"wh1\"";
                        if (list.some((l) => typeof l.name_en !== "string" || !l.name_en.trim())) return "Every location needs an English name (name_en)";
                        if (list.some((l) => typeof l.name_ar !== "string" || !l.name_ar.trim())) return "Every location needs an Arabic name (name_ar)";
                        const ids = list.map((l) => String(l.id).trim().toLowerCase());
                        if (new Set(ids).size !== ids.length) return "Location ids must be unique";
                        return undefined;
                    },
                },
                ...flags,
            ],
        },
        {
            collection: "ctc_commodities", label: "Commodity", pluralLabel: "Commodities", fields: [
                ...named,
                { key: "measurementUnit", label: "Unit of measurement", type: "text", required: true },
                { key: "price", label: "Indicative price", type: "number" },
                { key: "currencyId", label: "Currency", type: "select", options: currencyOptions },
                ...flags,
            ],
        },
        {
            collection: "ctc_currencies", label: "Currency", pluralLabel: "Currencies", idField: "shortName", fields: [
                { key: "shortName", label: "ISO code", type: "text", required: true },
                ...named,
                { key: "decimalPoint", label: "Decimal places", type: "number", required: true },
                { key: "rates.mid", label: "Fallback exchange rate (KWD per unit)", type: "number", description: "Used when no exchange rate is available for this currency in the portal." },
                { key: "rates.buy", label: "Buying rate", type: "number", listHidden: true },
                { key: "rates.sell", label: "Selling rate", type: "number", listHidden: true },
                ...flags,
            ],
        },
        {
            collection: "ctc_commissions", label: "Commission profile", pluralLabel: "Commission profiles", modalSize: "70rem" /* room for the ranges table */, beforeDelete: commissionBeforeDelete, deleteNote: commissionDeleteNote, serverDelete: commissionServerDelete, fields: [
                ...named,
                { key: "currencyId", label: "Currency", type: "select", required: true, options: currencyOptions, half: true },
                { key: "tradingType", label: "Trading type", type: "text", half: true },
                { key: "isOnRange", label: "is On Range", type: "bool" },
                {
                    key: "ranges", label: "Commission ranges", type: "ranges", required: true, listHidden: false,
                    // Every problem the editor shows blocks saving (the server refuses only hard errors, so old data still calculates)
                    validate: (value) => { const c = ctcRangesCheck(value); return c.errors[0] ?? c.warnings[0]; },
                },
                ...flags,
            ],
        },
        {
            collection: "ctc_bank_accounts", label: "Bank account", pluralLabel: "Bank accounts", fields: [
                ...named,
                { key: "accountNumber", label: "Account number", type: "text", required: true },
                { key: "currencyId", label: "Currency", type: "select", required: true, options: currencyOptions },
                { key: "availableBalance", label: "Available balance", type: "number" },
                ...flags,
            ],
        },
    ];

    // Menu counts: active records (not retired / deleted) of the lists this page already loads
    const active = (list?: { retired?: boolean; deleted?: boolean }[]) => list?.filter((r) => !r.deleted && !r.retired).length;
    const counts: Record<string, number | undefined> = {
        ctc_customers: active(customers as never), ctc_customer_groups: active(groups), ctc_brokers: active(brokers as never),
        ctc_commodities: active(commodities), ctc_currencies: active(currencies), ctc_commissions: active(commissions), ctc_bank_accounts: active(bankAccounts),
    };
    const sections = BACK_OFFICE_SECTIONS.map((sec) => ({ ...sec, items: sec.items.filter((it) => it.id === "attachments" || it.id === "dealers" || specs.some((sp) => sp.collection === it.id)) }));
    const current = sections.flatMap((sec) => sec.items).find((it) => it.id === section) ?? sections[0].items[0];
    const spec = specs.find((sp) => sp.collection === current.id);

    return (
        <Box style={{ display: "flex", gap: "var(--mantine-spacing-lg)", alignItems: "flex-start" }} className="ctc-bo">
            {/* Grouped side menu; collapses to an icon rail (remembered in this browser) */}
            <Paper withBorder radius="md" p="xs" className="ctc-bo-menu" style={{ width: collapsed ? 64 : 240 }}>
                {sections.map((sec, i) => (
                    <Box key={sec.group} mt={i ? "sm" : 0} pt={i ? "xs" : 0} style={i ? { borderTop: "1px solid var(--mantine-color-gray-2)" } : undefined}>
                        {!collapsed && <Text size="xs" fw={700} c="dimmed" tt="uppercase" px="sm" py={4} style={{ whiteSpace: "nowrap" }}>{sec.group}</Text>}
                        {sec.items.map((it) => {
                            const isActive = it.id === current.id;
                            const link = (
                                <NavLink key={it.id} label={collapsed ? undefined : it.label} leftSection={it.icon} active={isActive} variant="light" className="ctc-bo-link"
                                    aria-label={it.label}
                                    rightSection={!collapsed && counts[it.id] !== undefined ? <Text size="xs" c={isActive ? "blue" : "dimmed"}>{counts[it.id]}</Text> : undefined}
                                    onClick={() => { lastSection = it.id; setSection(it.id); }} />
                            );
                            return collapsed ? (
                                <Tooltip key={it.id} label={counts[it.id] !== undefined ? `${it.label} · ${counts[it.id]}` : it.label} position="right" withArrow>{link}</Tooltip>
                            ) : link;
                        })}
                    </Box>
                ))}
                <Box mt="sm" pt="xs" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
                    <NavLink label={collapsed ? undefined : "Collapse menu"} aria-label={collapsed ? "Expand menu" : "Collapse menu"} className="ctc-bo-link" c="dimmed"
                        leftSection={collapsed ? <IconLayoutSidebarLeftExpand size={18} /> : <IconLayoutSidebarLeftCollapse size={18} />}
                        onClick={() => setCollapsed(!collapsed)} />
                </Box>
            </Paper>
            {/* The selected screen (fades in when the section changes) */}
            <div key={current.id} style={{ flex: 1, minWidth: 0 }}>
                <CtcPanel icon={current.icon} title={current.label} description={current.description}>
                    {spec ? <MasterDataTable spec={spec} /> : current.id === "attachments" ? <CtcAttachmentTemplates /> : <CtcDealers />}
                </CtcPanel>
            </div>
            <style>{`
                .ctc-bo-menu { position: sticky; top: 16px; flex: none; overflow: hidden; transition: width 200ms ease; }
                .ctc-bo-link { border-radius: 8px; transition: background-color 150ms ease, color 150ms ease; white-space: nowrap; }
                @media (max-width: 48em) {
                    .ctc-bo { flex-direction: column; align-items: stretch !important; }
                    .ctc-bo-menu { position: static; width: auto !important; }
                }
            `}</style>
        </Box>
    );
};

export default CtcBackOffice;
